import type { ReactNode } from 'react';
import { ChevronLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

import { cn } from '@/lib/utils';
import { DeckSurface } from './DeckSurface';

interface DeckPageHeaderProps {
  /** Micro-label above the title — which part of the console this is. */
  eyebrow?: string;
  title: string;
  description: string;
  /** Buttons, right-aligned; wraps under the title on a phone. */
  actions?: ReactNode;
  /** Renders a back link above everything else. */
  backTo?: { to: string; label: string };
  /**
   * A crest, logo or initials tile, left of the title.
   *
   * For a screen that is about one named record rather than about a section of
   * the console — it is what tells two customers of similar name apart before
   * anybody has read either name.
   */
  mark?: ReactNode;
  /**
   * Anything that belongs to the heading rather than to the page under it — a
   * wizard's progress rail, a record's code and status.
   *
   * Drawn on the banner's tinted sheet rather than on a card, so anything put
   * here wants a solid `bg-surface` behind it to keep its contrast.
   */
  children?: ReactNode;
  className?: string;
  compact?: boolean;
}

/**
 * The banner every platform screen other than the overview opens with.
 *
 * The overview has `DeckHero`, which is the same lit band carrying a greeting,
 * live figures and an account card. This is its short form: the same light, the
 * same eyebrow with its breathing dot, the same type — just the heading of a
 * working screen rather than a front page.
 *
 * Deliberately carries no counts of its own. On the lists below, every number
 * worth printing is already a tab badge or a filter chip two inches lower down,
 * and a banner repeating them is a second copy to keep in step for no gain.
 *
 * Not `components/PageHeader`: that one is the plain institute-side heading and
 * eleven screens are drawn with it. This is the banded form — the same palette,
 * carrying a mark and a row of record facts — and it only exists here.
 */
export function DeckPageHeader({
  eyebrow,
  title,
  description,
  actions,
  backTo,
  mark,
  children,
  className,
  compact = false,
}: DeckPageHeaderProps) {
  return (
    <DeckSurface
      className={cn(
        'rounded-[20px] border border-accent-line shadow-card',
        compact ? 'px-4 py-3 sm:px-6 sm:py-3.5' : 'px-5 py-5 sm:px-7 sm:py-6',
        className,
      )}
    >
      {backTo && (
        <Link
          to={backTo.to}
          className={cn(
            'inline-flex items-center gap-1 text-sm text-ink-3 transition-colors hover:text-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-accent',
            compact ? 'mb-2' : 'mb-3',
          )}
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          {backTo.label}
        </Link>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-3">
          {mark && (
            <span
              className={cn(
                'grid shrink-0 place-items-center overflow-hidden border border-line bg-surface',
                compact ? 'h-11 w-11 rounded-xl' : 'h-14 w-14 rounded-[14px]',
              )}
            >
              {mark}
            </span>
          )}

          <div className="min-w-0">
            {eyebrow && (
              <span className="flex w-fit items-center gap-2 rounded-pill border border-accent-line bg-surface px-3 py-0.5 text-2xs font-bold uppercase tracking-label text-accent-2">
                <span className="relative grid h-2 w-2 place-items-center" aria-hidden>
                  <span className="absolute inset-0 animate-glow rounded-full bg-accent" />
                  <span className="h-1 w-1 rounded-full bg-accent" />
                </span>
                {eyebrow}
              </span>
            )}

            <h1
              className={cn(
                'truncate font-[750] tracking-tighter text-ink',
                compact ? 'text-xl' : 'text-2xl',
                eyebrow ? (compact ? 'mt-1' : 'mt-2.5') : 'mt-0',
              )}
            >
              {title}
            </h1>
            <p className={cn('truncate text-ink-2', compact ? 'mt-0.5 text-xs sm:text-sm max-w-xl' : 'mt-1 max-w-xl text-sm')}>
              {description}
            </p>
          </div>
        </div>

        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>

      {children && (
        <div className="mt-5 border-t border-accent-line pt-4 [scrollbar-width:none]">{children}</div>
      )}
    </DeckSurface>
  );
}
